import { getChatDialogListQuery } from '../model/filters';
import type { ChatDialogMessageResponse, ChatDialogResponse } from '@/core/generated/api';
import { mergeChatDialogMessages, mergeChatDialogs } from '../model/selectors';
import type { ChatDialogData, ChatDialogSnapshot, ChatDialogTransport } from '../model/types';
import { chatDialogService } from './chatDialogService';

const PAGE_SIZE = 30;

/** Загружает актуальное рабочее пространство переписки */
export const loadChatDialogSnapshot = async (
    state: ChatDialogData,
    isCurrent: () => boolean,
    service: ChatDialogTransport = chatDialogService,
): Promise<ChatDialogSnapshot | null> => {
    /** Загружает показанную часть списка диалогов */
    const loadDialogs = async () => {
        let dialogs: ChatDialogResponse[] = [];
        let offset: number | null = 0;
        let matchedAppUserIds: string[] | null = null;
        let totalCount: number | null = null;
        const count = Math.max(PAGE_SIZE, state.dialogs.length);

        do {
            const page = await service.getDialogs({
                ...getChatDialogListQuery(state),
                offset: offset ?? 0,
                limit: PAGE_SIZE,
            });

            if (!isCurrent()) return null;
            dialogs = mergeChatDialogs(dialogs, page.items ?? []);
            matchedAppUserIds = page.matchedAppUserIds ?? null;
            totalCount = page.totalCount ?? null;
            const next = page.nextOffset ?? null;

            if (next !== null && next <= (offset ?? 0)) {
                throw new Error('Не удалось продолжить загрузку диалогов');
            }

            offset = next;
        } while (offset !== null && dialogs.length < count);

        return {
            dialogs,
            matchedAppUserIds,
            totalCount,
            nextOffset: offset
        };
    };

    /** Загружает показанную часть истории с новыми сообщениями */
    const loadMessages = async () => {
        const dialogId = state.selectedDialog?.id;

        if (!dialogId) return {
            messages: null,
            nextBeforeSequence: null,
            nextAfterSequence: null
        };
        const oldest = state.messages[0]?.sequence;
        let messages: ChatDialogMessageResponse[] = [];
        let cursor: number | null = null;
        let nextAfterSequence: number | null = null;

        do {
            const page = await service.getMessages({
                dialogId,
                beforeSequence: cursor ?? undefined,
                limit: PAGE_SIZE,
            });

            if (!isCurrent()) return null;
            if (cursor === null) nextAfterSequence = page.nextAfterSequence ?? null;
            messages = mergeChatDialogMessages(page.items ?? [], messages);
            const next = page.nextBeforeSequence ?? null;

            if (next !== null && cursor !== null && next >= cursor) {
                throw new Error('Не удалось продолжить загрузку истории');
            }

            cursor = next;
        } while (cursor !== null && oldest !== undefined && (messages[0]?.sequence ?? Infinity) > oldest);

        return {
            messages,
            nextBeforeSequence: cursor,
            nextAfterSequence
        };
    };

    const [dialogs, history, counters] = await Promise.all([
        loadDialogs(),
        loadMessages(),
        service.getCounters(),
    ]);

    if (!isCurrent() || !dialogs || !history) return null;

    return {
        ...dialogs,
        ...history,
        counters
    };
};
